<template>
  <section class="d-grid gap-3" data-test="reporte-deuda-responsable">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h5 mb-1">Deuda por responsable</h2>
        <p class="text-muted small mb-0">A quién hay que llamar, con su teléfono y su saldo.</p>
      </div>
      <button class="btn btn-outline-dark btn-sm" data-test="btn-csv"
              :disabled="loading || !filas.length" @click="exportar">
        <i class="bi bi-download me-1"></i>Exportar CSV
      </button>
    </header>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-auto">
            <label class="form-label small mb-1" for="d-corte">Fecha de corte</label>
            <input id="d-corte" v-model="filtros.fecha_corte" type="date"
                   class="form-control form-control-sm" data-test="input-corte">
          </div>
          <div class="col-auto">
            <div class="form-check mt-3">
              <input id="d-vencidas" v-model="filtros.solo_vencidas" class="form-check-input"
                     type="checkbox" data-test="chk-vencidas">
              <label class="form-check-label small" for="d-vencidas">Sólo vencidas</label>
            </div>
          </div>
          <div class="col-auto">
            <button class="btn btn-dark btn-sm" data-test="btn-buscar"
                    :disabled="loading" @click="cargar">
              {{ loading ? 'Buscando...' : 'Buscar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mb-0" data-test="error">{{ error }}</div>

    <div v-if="totales" class="row g-3">
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Responsables con deuda</div>
          <div class="h5 mb-0" data-test="total-responsables">{{ totales.cantidad_responsables }}</div>
        </div></div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Saldo vencido</div>
          <div class="h5 mb-0 text-danger" data-test="total-vencido">
            {{ formatoMonto(totales.saldo_vencido) }}
          </div>
        </div></div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Saldo total</div>
          <div class="h5 mb-0" data-test="total-saldo">{{ formatoMonto(totales.saldo_total) }}</div>
        </div></div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-sm mb-0" data-test="tabla">
          <thead>
            <tr>
              <th>Responsable</th><th>Teléfono</th>
              <th class="text-end">Suscripciones</th><th class="text-end">Cuotas</th>
              <th class="text-end">Vencidas</th><th>Más antigua</th>
              <th class="text-end">Saldo vencido</th><th class="text-end">Saldo total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in filas" :key="f.cliente_id">
              <td>{{ f.responsable_nombre || '—' }}</td>
              <td class="small">{{ f.telefono || '—' }}</td>
              <td class="text-end">{{ f.suscripciones }}</td>
              <td class="text-end">{{ f.cuotas }}</td>
              <td class="text-end">{{ f.cuotas_vencidas }}</td>
              <td class="small">{{ f.vencimiento_mas_antiguo?.slice(0, 10) || '—' }}</td>
              <td class="text-end text-danger">{{ formatoMonto(f.saldo_vencido) }}</td>
              <td class="text-end">{{ formatoMonto(f.saldo_total) }}</td>
              <td class="text-end">
                <!-- El estado de cuenta ya existe (E5) y es la pantalla que
                     responde «¿qué debe exactamente?». No se duplica acá. -->
                <router-link v-if="f.cliente_id" class="btn btn-outline-secondary btn-sm"
                             :to="`/cobranza/estado-cuenta/${f.cliente_id}`"
                             data-test="link-estado-cuenta">
                  Estado de cuenta
                </router-link>
              </td>
            </tr>
            <tr v-if="!filas.length && !loading">
              <td colspan="9" class="text-center text-muted py-4" data-test="vacio">
                No hay responsables con deuda para esta fecha de corte.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
// N3 — Deuda por responsable. RG-F6.

import { ref, onMounted } from 'vue'
import reportsService from '@/services/reportsService'

const filas = ref([])
const totales = ref(null)
const loading = ref(false)
const error = ref('')
const filtros = ref({ fecha_corte: '', solo_vencidas: false })

const formatoMonto = (v) =>
  new Intl.NumberFormat('es-PY', { maximumFractionDigits: 0 }).format(Number(v) || 0)

async function cargar () {
  loading.value = true
  error.value = ''
  try {
    const data = await reportsService.getDeudaPorResponsable(filtros.value)
    filas.value = data.filas
    totales.value = data.totales
  } catch (e) {
    error.value = e?.response?.data?.message || 'No se pudo cargar el reporte.'
  } finally {
    loading.value = false
  }
}

async function exportar () {
  try {
    await reportsService.descargarCsv('deuda-por-responsable', filtros.value, 'deuda-por-responsable')
  } catch (e) {
    error.value = 'No se pudo exportar el CSV.'
  }
}

onMounted(cargar)
</script>
